import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft01Icon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { DashedGrid } from "@/components/ui/decorative-patterns";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** Kept for API compatibility — no longer rendered (eyebrow row removed). */
  eyebrow: string;
  /** Kept for API compatibility — no longer rendered. */
  icon?: IconSvgElement;
  /** Main page heading. Can be a string or ReactNode (e.g. with a status badge). */
  title: React.ReactNode;
  /** Optional supporting copy under the title. */
  description?: React.ReactNode;
  /** Optional CTA on the right (button, dialog trigger, etc.). */
  action?: React.ReactNode;
  /** When set, shows a back-arrow button to this route. */
  backTo?: string;
  /** Optional filter row / tabs rendered as a solid gray bottom shelf — the
   *  white title row rides UP over the shelf's top edge. */
  tabs?: React.ReactNode;
}

/**
 * Shared page header — two-layer card (ticketbalie house pattern, Prado green):
 * a solid opaque gray base shell with a white title row riding up over it
 * (rounded below so the gray reads as a layer sliding under), dashed-grid
 * wash top-right, restrained `text-xl sm:text-2xl` title, solid gray bottom
 * shelf for tabs/filters. No eyebrow row. Same props API — UI-only.
 */
export function PageHeader({
  title,
  description,
  action,
  backTo,
  tabs,
}: PageHeaderProps) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-border/60 bg-[hsl(140_14%_97%)] dark:bg-[hsl(140_8%_9%)]">
      {/* White title row — rounded below so the gray base reads as sliding under */}
      <div
        className={cn(
          "relative z-10 flex flex-col gap-4 overflow-hidden bg-card px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5",
          tabs && "rounded-b-xl border-b border-border/60",
        )}
      >
        <DashedGrid position="top-right" />
        <div className="relative z-10 flex min-w-0 flex-1 items-center gap-3">
          {backTo && (
            <Button
              variant="ghost"
              size="icon"
              render={<Link to={backTo} />}
              className="-ml-2 h-7 w-7 shrink-0"
              aria-label="Back"
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
            </Button>
          )}
          <div className="min-w-0">
            <h1 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">
              {title}
            </h1>
            {description && (
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        {action && (
          <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2 sm:ml-auto">
            {action}
          </div>
        )}
      </div>
      {tabs && (
        <div className="flex flex-wrap items-center gap-2 px-5 pb-3 pt-2.5 sm:px-6">
          {tabs}
        </div>
      )}
    </div>
  );
}
